import React, { useState } from "react";

// Define the assessments and questions
const ASSESSMENTS = [
  {
    key: "stress",
    name: "Stress (PSS-4)",
    questions: [
      "In the last month, how often have you felt that you were unable to control the important things in your life?",
      "In the last month, how often have you felt confident about your ability to handle your personal problems?",
      "In the last month, how often have you felt that things were going your way?",
      "In the last month, how often have you felt difficulties were piling up so high that you could not overcome them?"
    ],
    options: ["Never", "Almost Never", "Sometimes", "Fairly Often", "Very Often"]
  },
  {
    key: "vitd",
    name: "Vitamin D Risk",
    questions: [
      "Do you spend less than 15 minutes per day in direct sunlight?",
      "Do you regularly use sunscreen or cover your skin outdoors?",
      "Do you have darker skin, or live in a northern country?",
      "Do you rarely eat oily fish, eggs, or fortified foods?",
      "Do you often feel tired or have muscle aches?"
    ],
    options: ["No", "Yes"]
  },
  {
    key: "anxiety",
    name: "Anxiety (GAD-2)",
    questions: [
      "Over the last 2 weeks, how often have you felt nervous, anxious, or on edge?",
      "Over the last 2 weeks, how often were you unable to stop or control worrying?"
    ],
    options: [
      "Not at all", "Several days", "More than half the days", "Nearly every day"
    ]
  }
];

export default function AssessmentFlowPage({ onComplete, user }) {
  const [currentAssessment, setCurrentAssessment] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  // Initialize answers with nulls for each question of each assessment
  const [answers, setAnswers] = useState(
    ASSESSMENTS.map(a => Array(a.questions.length).fill(null))
  );
  const [inProgress, setInProgress] = useState(false);

  const assessment = ASSESSMENTS[currentAssessment];
  const question = assessment.questions[currentQuestion];
  const options = assessment.options;

  // Calculate total questions across all assessments
  const totalQuestions = ASSESSMENTS.reduce((acc, a) => acc + a.questions.length, 0);
  // Calculate the current question index for progress display
  const currentIndex = ASSESSMENTS
    .slice(0, currentAssessment)
    .reduce((acc, a) => acc + a.questions.length, 0) + currentQuestion + 1;

  // Handler when user selects an answer option
  const handleSelect = idx => {
    if (inProgress) return; // prevent clicks while processing
    setInProgress(true);

    // Create a deep clone of answers to update the current answer
    const updated = answers.map(arr => [...arr]);
    updated[currentAssessment][currentQuestion] = idx;
    setAnswers(updated);

    // Simulate short delay for smooth UI transition
    setTimeout(() => {
      setInProgress(false);
      // Advance to next question or assessment, or finish if last question of last assessment
      if (currentQuestion < assessment.questions.length - 1) {
        // Move to next question in the current assessment
        setCurrentQuestion(currentQuestion + 1);
      } else if (currentAssessment < ASSESSMENTS.length - 1) {
        // Move to first question of next assessment
        setCurrentAssessment(currentAssessment + 1);
        setCurrentQuestion(0);
      } else {
        // All questions answered; flatten and send answers via onComplete callback
        const flatAnswers = {
          // Stress answers
          stress_1: updated[0][0],
          stress_2: updated[0][1],
          stress_3: updated[0][2],
          stress_4: updated[0][3],
          // Vitamin D answers
          vitd_1: updated[1][0],
          vitd_2: updated[1][1],
          vitd_3: updated[1][2],
          vitd_4: updated[1][3],
          vitd_5: updated[1][4],
          // Anxiety answers
          anxiety_1: updated[2][0],
          anxiety_2: updated[2][1],
        };
        onComplete(flatAnswers, user);
      }
    }, 200);
  };

  return (
    <div
      style={{
        maxWidth: 500,
        margin: "32px auto",
        background: "#fff",
        borderRadius: 24,
        boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
        padding: 32,
      }}
      aria-live="polite"
      aria-label="Assessment questionnaire"
    >
      <h2>Let's get to know you</h2>
      <div style={{ marginBottom: 10, fontSize: 15, color: "#888" }} aria-atomic="true">
        {assessment.name}
      </div>
      <div style={{ marginBottom: 8, fontWeight: 500 }}>
        Question {currentIndex} of {totalQuestions}
      </div>
      <p style={{ minHeight: 48, fontWeight: "600" }}>{question}</p>
      <ul style={{ listStyle: "none", padding: 0 }}>
        {options.map((opt, idx) => (
          <li key={opt}>
            <button
              type="button"
              style={{
                width: "100%",
                padding: "14px 0",
                margin: "8px 0",
                borderRadius: 16,
                border: "1px solid #eee",
                background: inProgress && idx === answers[currentAssessment][currentQuestion] ? "#ddd" : "#f7f7f7",
                fontWeight: 600,
                cursor: inProgress ? "not-allowed" : "pointer",
                opacity: inProgress ? 0.7 : 1,
              }}
              disabled={inProgress}
              aria-pressed={answers[currentAssessment][currentQuestion] === idx}
              onClick={() => handleSelect(idx)}
            >
              {opt}
            </button>
          </li>
        ))}
      </ul>
      <p style={{ fontSize: "0.85em", color: "#888", marginTop: 16 }}>
        Source: {assessment.name}
      </p>
    </div>
  );
}
